import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Users, Palette, Layout, DollarSign, BarChart3, Search } from 'lucide-react';

const FBGoogleAds = () => {
  const quoteRef = useRef(null);
  const contentRef = useRef(null);
  const processRef = useRef(null);
  
  const isQuoteInView = useInView(quoteRef, { once: true, margin: "-100px" });
  const isContentInView = useInView(contentRef, { once: true, margin: "-100px" });
  const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });

  const services = [
    { title: "Paid Search (Google Ads)", icon: Search },
    { title: "Social Ads (Facebook & Instagram)", icon: Layout },
    { title: "Retargeting & Remarketing", icon: Target },
    { title: "ROAS-driven Campaigns", icon: DollarSign },
  ];

  const processSteps = [
    {
      title: "Audience Research & Targeting",
      description: "Identifying and targeting your ideal customers based on behaviors, interests, and demographics.",
      icon: Users
    },
    {
      title: "Ad Creative & Copy",
      description: "Developing compelling ad creatives and persuasive copy that align with your brand while catching the eye of potential customers.",
      icon: Palette
    },
    {
      title: "Campaign Structure & Setup",
      description: "Building a well-structured campaign across Google and Facebook, ensuring the right mix of reach and retargeting for effective budget use.",
      icon: Layout
    },
    {
      title: "Budget Management",
      description: "Allocating your ad spend wisely, focusing on maximizing ROAS (Return on Ad Spend) through well-optimized campaigns.",
      icon: DollarSign
    },
    {
      title: "Data-Driven Optimization",
      description: "Continuously optimizing campaigns based on performance data, adjusting bidding strategies, targeting, and creatives to improve conversion rates.",
      icon: BarChart3
    }
  ];

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        {/* Breadcrumb */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-8 text-gray-600"
        >
          
        </motion.div>

        {/* Quote Section */}
        <motion.div
          ref={quoteRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isQuoteInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8">
  <span className="bg-[#f4b400] bg-clip-text text-transparent pb-2 leading-relaxed inline-block">
    FB & Google Ads
  </span>
</h1>
          <p className="mt-10 text-xl md:text-2xl italic text-gray-600">
            "In our factory, we make lipstick. In our advertising, we sell hope."
            <span className="block mt-2 text-base">— Peter Nivio Zarlenga</span>
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-4 text-center"
            >
              <service.icon className="w-8 h-8 mx-auto mb-2 text-[#ffd54f]" />
              <h3 className="text-sm md:text-base font-medium text-gray-800">{service.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isContentInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16 text-gray-700"
        >
          <p className="text-lg leading-relaxed mb-8">
            Give them what they want — how they want it. This is essentially what paid ads like Facebook and Google do (if done well…) Good ads supercharge your sales with targeted customers that are genuinely interested. Whether it's Google, Facebook, or Instagram, <span className="font-bold text-[#f4b400]">GROVIYO</span> will amplify your reach to the perfect audience.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li>Strategy creation and campaign planning</li>
            <li>Competitor and industry analysis</li>
            <li>Ad copywriting</li>
            <li>Ad and landing page design</li>
            <li>Bid management and optimization</li>
            <li>Campaign performance testing and refinement</li>
            <li>Advanced tracking and reporting</li>
          </ul>
        </motion.div>

        {/* Process Section */}
        <motion.div
          ref={processRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Process</h2>
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isProcessInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <step.icon className="w-6 h-6 text-[#ffd54f]" />
                  <h3 className="text-xl font-semibold text-[#ffd54f]">{step.title}</h3>
                </div>
                <p className="text-gray-600 mt-2">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="p-8 bg-white/80 backdrop-blur-sm rounded-2xl">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">GROVIYO WILL:</h2>
            <p className="text-lg text-gray-700 mb-6">
              Supercharge your reach and conversions through strategic, data-driven paid advertising campaigns that target the right audience at the right time.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FBGoogleAds;